/**
 * 🔒 PROTECTED GUEST-FACING MODULE — DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 * Locked under /app/memory/PROTECTED_GUEST_FACING.md (Feb 2026).
 * The greeting rule defined here is the single source of truth consumed by
 * ComposeTab, InviteLandingPage, ExperiencePage, RSVPPublicModule, AND mirrored
 * in backend/server.py::_resolve_party_display_name.
 * Read /app/memory/PROTECTED_GUEST_FACING.md before proposing edits.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * resolveGuestGreeting.js
 * 
 * Resolves a personalized, guest-facing greeting name from party/guest data.
 * 
 * Priority (highest first) — Feb 2026 (multi-surface alignment):
 * 1. Admin-typed group_name on any member (set via Profile editor) → verbatim.
 *    This is the strongest signal of admin intent, beating party_name even
 *    when party_name matches the auto-stamp pattern (e.g. admin who typed
 *    "Valenciano Party" as their explicit group name).
 * 2. Admin-set party_name (when present AND not auto-stamped) → verbatim.
 *    Auto-stamped party_names from CSV import (e.g. "Lavarias Party",
 *    "Mendoz & Mendoza Party") match /\bParty\s*$/i and are SKIPPED.
 * 3. Solo guest → first name only
 * 4. Multi-guest fallback — all share the same last name → "{Last Name} Family"
 * 5. Multi-guest fallback — non-family → "{Primary First Name} and Guests"
 * 
 * Same function is consumed by ComposeTab (admin send path), InviteLandingPage,
 * ExperiencePage, and RSVPPublicModule so every surface shows the same string
 * for the same target.
 * 
 * Rules:
 * - Never use the word "Party" in output (auto-stamps are filtered out)
 * - Never leak backend grouping terminology
 */

// Heuristic for auto-stamped party_name strings produced by CSV-import path.
// Pattern: ends with the literal word "Party". Admin-typed group names like
// "Carol & Lito", "The Starks", "The Tomines Family" never match this.
export function isAutoStampedPartyName(name) {
  return /\bParty\s*$/i.test((name || '').trim());
}

export function resolveGuestGreeting(guests, partyName, primaryGuest) {
  // 1. Admin-typed group_name from any member wins unconditionally. Profile
  //    editor sets this, so its presence proves admin touched it. Beats the
  //    auto-stamp filter — admin who explicitly typed "Valenciano Party" as
  //    their group name keeps that exact string.
  const adminGroupName = (guests || [])
    .map(g => (g?.group_name || '').trim())
    .find(n => n);
  if (adminGroupName) return adminGroupName;

  // 2. Admin-set party_name wins verbatim — but only if it doesn't look like
  //    an auto-stamped CSV-import string. Auto-stamped values fall through.
  const pn = (partyName || '').trim();
  if (pn && !isAutoStampedPartyName(pn)) {
    return pn;
  }

  if (!guests || guests.length === 0) {
    return primaryGuest?.first_name || 'Friend';
  }

  // 2. Solo guest with NO admin party_name → first name only.
  if (guests.length === 1) {
    return guests[0].first_name || 'Friend';
  }

  // 3. Fallback — all guests share the same last name → "{Last Name} Family"
  const lastNames = guests
    .map(g => (g.last_name || '').trim())
    .filter(Boolean);
  const uniqueLower = [...new Set(lastNames.map(n => n.toLowerCase()))];
  if (uniqueLower.length === 1 && lastNames.length === guests.length) {
    return `${lastNames[0]} Family`;
  }

  // 4. Fallback — non-family multi-guest → "{Primary First Name} and Guests"
  const primary = primaryGuest || guests[0];
  return `${primary?.first_name || 'Friend'} and Guests`;
}
